import React, { useEffect, useMemo } from 'react';
import moment from 'moment';
import { Button } from 'semantic-ui-react';
import {
  ClockCircleOutlined,
  ExportOutlined,
  CloseOutlined
} from '@ant-design/icons';
import ModalSemantic from 'components/modalSemantic';
import {
  useIsBookedAuditHistory,
  AuditVariant,
  IIsBookedAuditRow
} from 'api/graphql/v2/hooks/isBookedAudit';
import Loader from 'views/components/ui/content/Loader';
import { AuditModalContent } from './IsBookedBlockAuditModal.style';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  availabilityId: number;
  variant?: AuditVariant;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const AVATAR_COLORS = [
  '#2f9d7b',
  '#6c5ce7',
  '#e67e22',
  '#2980b9',
  '#c0392b',
  '#16a085'
];

const ACTION_LABELS: { [key: string]: string } = {
  CREATE: 'Event booked',
  CANCEL: 'Event cancelled',
  RESCHEDULE: 'Rescheduled in',
  RESCHEDULE_CANCEL: 'Rescheduled out',
  RESCHEDULE_CREATE: 'Rescheduled in',
  AVAILABILITY_UPDATE: 'Availability update',
  CRON: 'Daily cron sweep',
  MANUAL: 'Manual change'
};

const formatTime = (time?: string) => {
  if (!time) return '';
  return moment(time, ['HH:mm:ss', 'HH:mm']).format('h:mm a');
};

const actionLabel = (action: string) => ACTION_LABELS[action] || action;

const isSystemActor = (actionBy: string) =>
  !actionBy || actionBy.startsWith('system') || actionBy === 'N/A';

const actorInitials = (actionBy: string) => {
  if (isSystemActor(actionBy)) return 'SY';
  const namePart = actionBy.split('@')[0];
  return namePart.slice(0, 2).toUpperCase();
};

const actorName = (actionBy: string) => {
  if (isSystemActor(actionBy)) return 'System';
  return actionBy.split('@')[0];
};

const avatarColor = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const StatePill: React.FC<{ value: boolean }> = ({ value }) => (
  <span className={`pill ${value ? 'booked' : 'open'}`}>
    <span className="dot">&#9679;</span>
    {value ? 'Booked' : 'Open'}
  </span>
);

const YesNoPill: React.FC<{ value: boolean }> = ({ value }) => (
  <span className={`pill ${value ? 'yes' : 'no'}`}>
    <span className="dot">&#9679;</span>
    {value ? 'Yes' : 'No'}
  </span>
);

const IsBookedBlockAuditModal: React.FC<Props> = ({
  isOpen,
  onClose,
  availabilityId,
  variant = 'provider'
}: Props) => {
  const {
    loadAuditHistory,
    auditRows,
    isLoadingAudit
  } = useIsBookedAuditHistory(variant);
  const isClient = variant === 'client';

  useEffect(() => {
    if (isOpen && availabilityId) {
      loadAuditHistory(availabilityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, availabilityId]);

  const headerRow: IIsBookedAuditRow | undefined = auditRows[0];

  const title = useMemo(() => {
    if (!headerRow) return 'isBookedBlock audit';
    const day = DAYS[headerRow.dayOfWeek] || '';
    const time =
      headerRow.startTime && headerRow.endTime
        ? `${formatTime(headerRow.startTime)} – ${formatTime(
            headerRow.endTime
          )}`
        : '';
    const who = headerRow.providerName || headerRow.clientName || '';
    return `${who} · ${day} ${time}`.trim();
  }, [headerRow]);

  return (
    <ModalSemantic
      open={isOpen}
      onClose={onClose}
      modalWidth={isClient ? 1240 : 1160}
      trigger={<div />}
      content={
        <AuditModalContent>
          <button
            type="button"
            className="audit-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
          <div className="audit-header">
            <div className="audit-eyebrow">
              <ClockCircleOutlined /> isBookedBlock audit
            </div>
            <h2 className="audit-title">{title}</h2>
          </div>

          {isLoadingAudit ? (
            <Loader />
          ) : auditRows.length === 0 ? (
            <div className="audit-empty">
              No isBookedBlock transitions recorded for this availability.
            </div>
          ) : (
            <table className="audit-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Transition</th>
                  <th>Action</th>
                  <th>Created by</th>
                  <th>Event</th>
                  <th>OpensOn &#916;</th>
                  {isClient && <th>Non-BT booking</th>}
                </tr>
              </thead>
              <tbody>
                {auditRows.map(row => (
                  <tr key={row.id}>
                    <td>
                      <div className="when-primary">
                        {moment(row.changedAt).format('MMM D, YYYY')}
                      </div>
                      <div className="when-secondary">
                        {moment(row.changedAt).format('h:mm A')}
                        {' · '}
                        {moment(row.changedAt).fromNow()}
                      </div>
                    </td>
                    <td>
                      <div className="transition">
                        <StatePill value={row.previousValue} />
                        <span className="arrow">&#8594;</span>
                        <StatePill value={row.newValue} />
                      </div>
                    </td>
                    <td>
                      <span className="action-tag">
                        {actionLabel(row.action)}
                      </span>
                    </td>
                    <td>
                      <div className="actor">
                        <span
                          className="avatar"
                          style={{
                            backgroundColor: avatarColor(row.actionBy)
                          }}
                        >
                          {actorInitials(row.actionBy)}
                        </span>
                        <div className="actor-name">
                          {actorName(row.actionBy)}
                        </div>
                      </div>
                    </td>
                    <td>
                      {row.eventId ? (
                        <span className="event-link">
                          <ExportOutlined /> Event # {row.eventId}
                        </span>
                      ) : (
                        <span className="no-event">No event</span>
                      )}
                    </td>
                    <td>
                      {row.previousOpensOn !== row.opensOn ? (
                        <div className="opens-delta">
                          <span className="opens-old">
                            {row.previousOpensOn || 'None'}
                          </span>
                          <span className="arrow">&#8594;</span>
                          <span className="opens-new">
                            {row.opensOn || 'None'}
                          </span>
                        </div>
                      ) : (
                        <span className="muted">&#8211;</span>
                      )}
                    </td>
                    {isClient && (
                      <td>
                        {row.previousHasNonBTBooking !==
                        row.hasNonBTBooking ? (
                          <div className="transition">
                            <YesNoPill value={!!row.previousHasNonBTBooking} />
                            <span className="arrow">&#8594;</span>
                            <YesNoPill value={!!row.hasNonBTBooking} />
                          </div>
                        ) : (
                          <span className="muted">&#8211;</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </AuditModalContent>
      }
      actions={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%',
            padding: '4px 16px 20px'
          }}
        >
          <Button primary onClick={onClose}>
            Close
          </Button>
        </div>
      }
    />
  );
};

export default React.memo(IsBookedBlockAuditModal);
